import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, Check, Clock, CreditCard, DollarSign, Gift, Info, ShoppingBag, Truck, Zap } from "lucide-react";
import { useMenu, useOrderingStatus, usePlaceOrder, useLoyalty, useLoyaltyQuote, ORDERING_ENABLED } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { useCart } from "../lib/cart";
import { chf } from "../lib/format";
import { Header } from "../components/Header";

const WEEKDAYS = {
  fr: ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"],
  de: ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"],
};

// One unique client_order_id per checkout attempt, persisted so it stays identical across retries
// (and even across a reload) until an order succeeds — then it is cleared for the next genuine order.
const genClientOrderId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `web-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
const getClientOrderId = () => {
  let id = null;
  try { id = localStorage.getItem("client_order_id"); } catch {}
  if (!id) { id = genClientOrderId(); try { localStorage.setItem("client_order_id", id); } catch {} }
  return id;
};
const loadLastOrder = () => {
  try { return JSON.parse(localStorage.getItem("last_order") || "null"); } catch { return null; }
};

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const cart = useCart();
  const { user } = useAuth();
  const { data } = useMenu();
  const { data: ordering } = useOrderingStatus();
  const place = usePlaceOrder();

  // Fidelity (loyalty) — logged-in only; backend is the single source of truth for all amounts.
  const loyalty = useLoyalty(!!user);
  const loyaltyData = loyalty.data;
  const loyaltyQuote = useLoyaltyQuote(cart.items, !!user);
  const quoteData = loyaltyQuote.data;
  const loyaltyDiscount = quoteData?.discount || 0;
  const loyaltyPct = Math.round(((loyaltyData?.rate ?? 0.5) * 100));
  const submittedRef = useRef(false);
  const [lastOrder, setLastOrder] = useState(loadLastOrder);
  const typeSectionRef = useRef(null);
  const timeSectionRef = useRef(null);
  const scrollToSection = (ref) => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  const settings = data?.settings;
  const type = cart.orderType;

  const [timeMode, setTimeMode] = useState("asap");
  const [time, setTime] = useState("");
  const [chosenDay, setChosenDay] = useState(null);
  const [payment, setPayment] = useState("cash");
  const [ageOk, setAgeOk] = useState(false);
  const [saveAddress, setSaveAddress] = useState(true);
  const [savedAddrId, setSavedAddrId] = useState(null);
  const [f, setF] = useState({ first_name: "", last_name: "", phone: "", email: "", street: "", number: "", npa: "", city: "", instructions: "" });
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(f.email.trim());

  // Prefill from the logged-in account + saved addresses (read-only reads).
  useEffect(() => {
    if (!user) return;
    setF((p) => ({ ...p, first_name: p.first_name || user.first_name, last_name: p.last_name || user.last_name, phone: p.phone || user.phone, email: p.email || user.email || "" }));
    if (user.addresses.length && !savedAddrId) pickSavedAddress(user.addresses[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const pickSavedAddress = (id) => {
    setSavedAddrId(id);
    const a = user?.addresses.find((x) => x.id === id);
    if (a) setF((p) => ({ ...p, street: a.street, number: a.number, npa: a.npa, city: a.city, instructions: a.instructions || "" }));
    else setF((p) => ({ ...p, street: "", number: "", npa: "", city: "", instructions: "" }));
  };

  // --- Time selection (same rules as the Mobile app, from GET /settings/ordering) ---
  const days = useMemo(() => ordering?.days ?? [], [ordering]);
  const hasSlots = (d) => (type === "delivery" ? d.delivery_slots : d.pickup_slots).length > 0;
  const dayIdx = Math.max(0, chosenDay ? days.findIndex((d) => d.date === chosenDay) : days.findIndex(hasSlots));
  const day = days[dayIdx] ?? days[0];
  const isFutureDay = !!day && !day.is_today;
  const slots = useMemo(() => {
    if (day) return type === "delivery" ? day.delivery_slots : day.pickup_slots;
    return ordering ? (type === "delivery" ? ordering.delivery_slots : ordering.pickup_slots) : [];
  }, [ordering, type, day]);
  const asapAvailable = !ordering || (type === "pickup" ? ordering.asap_pickup ?? ordering.pickup_open : ordering.asap_delivery ?? ordering.delivery_open);
  const restaurantOpen = !ordering || (ordering.open_now ?? ordering.pickup_open);
  const asapFrom = !restaurantOpen ? ordering.asap_from : null;
  const closedNow = !restaurantOpen && !asapAvailable;
  const closedToday = !!ordering?.closed_today;
  useEffect(() => { if (!asapAvailable && timeMode === "asap") setTimeMode("scheduled"); }, [asapAvailable, timeMode]);
  const weekday = (iso) => WEEKDAYS[lang === "de" ? "de" : "fr"][new Date(iso + "T12:00:00").getDay()];
  const dayLabel = (d) => (d.is_today ? t("today") : d.is_tomorrow ? t("tomorrow") : `${weekday(d.date)} ${d.date.slice(8, 10)}.${d.date.slice(5, 7)}`);
  const pickDay = (i) => { setChosenDay(days[i].date); setTime(""); };

  // --- Totals ---
  const zone = settings?.delivery_zones?.find((z) => z.npa === f.npa.trim());
  const zoneMissing = type === "delivery" && !!settings?.delivery_zones?.length && f.npa.trim().length >= 4 && !zone;
  const minimum = zone?.minimum_order || settings?.minimum_order || 0;
  const goods = cart.subtotal + cart.extrasTotal;
  const fee = type === "delivery" && settings ? (settings.free_delivery_from && goods >= settings.free_delivery_from ? 0 : settings.delivery_fee) : 0;
  const total = (quoteData ? quoteData.total : goods) + fee;
  const belowMin = type === "delivery" ? goods < minimum : false;

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen bg-hm-surface">
        <Header />
        <div className="flex flex-col items-center gap-4 py-24 text-center">
          <ShoppingBag size={40} className="text-hm-muted" />
          <p className="font-display text-2xl text-hm-ink">{t("emptyCart")}</p>
          {lastOrder ? (
            <div className="flex flex-col items-center gap-3" data-testid="last-order-recovery">
              <p className="max-w-xs font-sans text-[13px] text-hm-ink" data-testid="last-order-recovery-text">{t("orderSentRecovery")}</p>
              <button
                type="button"
                data-testid="view-my-order-button"
                onClick={() => navigate(`/order/${lastOrder.id}`)}
                className="inline-flex items-center gap-1.5 rounded-full bg-hm-olive px-6 py-3 font-sans font-extrabold text-white transition-opacity hover:opacity-90"
              >
                {t("viewMyOrder")} #{lastOrder.order_number}
              </button>
            </div>
          ) : null}
          <button type="button" data-testid="checkout-browse-menu" onClick={() => navigate("/")} className="rounded-full bg-hm-brand px-6 py-3 font-sans font-extrabold text-white">
            {t("browseMenu")}
          </button>
        </div>
      </div>
    );
  }

  // Validation gate — identical rules to the Mobile app.
  const requestedTime = timeMode === "asap" ? "asap" : time;
  const valid =
    !!f.first_name.trim() &&
    !!f.phone.trim() &&
    emailOk &&
    (timeMode === "asap" ? asapAvailable : !!time && slots.includes(time)) &&
    (type === "pickup" || (!!f.street.trim() && !!f.npa.trim() && !!f.city.trim() && !zoneMissing)) &&
    (!cart.requiredAge || ageOk) &&
    !belowMin &&
    !closedToday &&
    cart.items.length > 0;

  // Human-readable requested time for the pre-submit summary block.
  const whenText =
    timeMode === "asap"
      ? `${t("asap")}${asapFrom ? ` · ${asapFrom}` : ""}`
      : `${isFutureDay && day ? `${dayLabel(day)} · ` : ""}${time || t("chooseTime")}`;

  // Real production order — sends exactly ONE POST /api/orders. Guarded against double submit.
  const submit = async () => {
    if (!ORDERING_ENABLED) { toast.error(t("orderingUnavailableTitle")); return; } // safety lock
    if (closedToday) { toast.error(t("closedToday")); return; } // backend blocks fully-closed calendar days
    if (place.isPending || submittedRef.current) return; // duplicate-submit protection
    if (!valid) {
      toast.error(cart.requiredAge && !ageOk ? t("ageRequired") : !emailOk && f.first_name.trim() && f.phone.trim() ? t("invalidEmail") : t("required"));
      return;
    }
    submittedRef.current = true;
    const clientOrderId = getClientOrderId(); // same id reused on every retry until success
    try {
      const order = await place.mutateAsync({
        type,
        items: cart.items,
        customer: { first_name: f.first_name.trim(), last_name: f.last_name.trim(), phone: f.phone.trim(), email: f.email.trim().toLowerCase() },
        payment_method: payment,
        address: type === "delivery" ? { street: f.street.trim(), number: f.number.trim(), npa: f.npa.trim(), city: f.city.trim(), instructions: f.instructions.trim() || undefined } : undefined,
        requested_time: requestedTime,
        requested_date: timeMode === "scheduled" && isFutureDay && day ? day.date : null,
        general_note: cart.generalNote.trim() || undefined,
        age_confirmed: ageOk,
        save_address: !!user && type === "delivery" && !savedAddrId && saveAddress,
        language: lang,
        marketing_consent: false,
        client_order_id: clientOrderId,
      });
      // Success path. Persist a recovery pointer FIRST so that even if navigation/rendering throws,
      // the customer still sees a "Voir ma commande #N" action instead of being tempted to resubmit.
      const lo = { id: order.id, order_number: order.order_number };
      try { localStorage.setItem("last_order", JSON.stringify(lo)); } catch {}
      setLastOrder(lo);
      try { localStorage.removeItem("client_order_id"); } catch {} // next genuine checkout gets a fresh id
      cart.addMyOrder(order.id);
      cart.clear();
      toast.success(t("orderPlaced"));
      navigate(`/order/${order.id}`, { replace: true, state: { order } });
    } catch (e) {
      // Failure: KEEP the cart AND the client_order_id (so a manual retry de-duplicates on the backend).
      submittedRef.current = false;
      if (e?.status) toast.error(e.message || t("required"));         // backend responded with an error -> safe, no order created
      else toast.error(t("networkUncertain"), { duration: 9000 });    // no response after 3 tries -> uncertain; do NOT silently resubmit
    }
  };

  return (
    <div className="min-h-screen bg-hm-surface pb-40">
      <Header />
      <div className="mx-auto w-full max-w-[760px] px-4">
        <div className="flex items-center gap-3 py-4">
          <button type="button" data-testid="checkout-back" onClick={() => navigate("/cart")} className="flex h-10 w-10 items-center justify-center rounded-full bg-hm-tertiary text-hm-ink">
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="font-display text-3xl text-hm-ink" data-testid="checkout-title">{t("checkout")}</h1>
            <p className="font-sans text-sm text-hm-muted">{cart.count} {t("items").toLowerCase()} · {chf(goods)}</p>
          </div>
        </div>

        {lastOrder ? (
          <div className="mt-2 flex items-start gap-3 rounded-2xl border border-hm-olive/30 bg-hm-olive/10 p-4" data-testid="last-order-recovery">
            <Check size={20} className="mt-0.5 flex-shrink-0 text-hm-olive" />
            <div className="flex-1">
              <p className="font-sans text-[13px] text-hm-ink" data-testid="last-order-recovery-text">{t("orderSentRecovery")}</p>
              <button
                type="button"
                data-testid="view-my-order-button"
                onClick={() => navigate(`/order/${lastOrder.id}`)}
                className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-hm-olive px-4 py-2 font-sans text-sm font-extrabold text-white transition-opacity hover:opacity-90"
              >
                {t("viewMyOrder")} #{lastOrder.order_number}
              </button>
            </div>
          </div>
        ) : null}

        {!ORDERING_ENABLED ? (
          <div className="mt-2 flex items-start gap-3 rounded-2xl border border-hm-error/30 bg-hm-error/10 p-4" data-testid="ordering-unavailable-banner">
            <AlertTriangle size={20} className="mt-0.5 flex-shrink-0 text-hm-error" />
            <div>
              <p className="font-sans text-sm font-extrabold text-hm-error" data-testid="ordering-unavailable-title">{t("orderingUnavailableTitle")}</p>
              <p className="mt-1 font-sans text-[13px] text-hm-ink" data-testid="ordering-unavailable-body">{t("orderingUnavailableBody")}</p>
            </div>
          </div>
        ) : null}

        {/* Order type */}
        <div ref={typeSectionRef} className="flex gap-2.5 scroll-mt-24" data-testid="checkout-order-type">
          {[
            { key: "pickup", Icon: ShoppingBag, label: t("pickup"), enabled: settings?.pickup_enabled !== false },
            { key: "delivery", Icon: Truck, label: t("delivery"), enabled: settings?.delivery_enabled !== false },
          ].map(({ key, Icon, label, enabled }) => {
            const on = type === key;
            return (
              <button
                key={key}
                type="button"
                disabled={!enabled}
                data-testid={`checkout-type-${key}`}
                onClick={() => cart.setOrderType(key)}
                className={`flex h-14 flex-1 items-center justify-center gap-2.5 rounded-2xl border-[1.5px] transition-all ${on ? "border-hm-inverse bg-hm-inverse text-white" : "border-hm-line bg-hm-card text-hm-ink"} ${!enabled ? "opacity-40" : ""}`}
              >
                <Icon size={18} className={on ? "text-white" : "text-hm-brand"} />
                <span className="font-sans text-sm font-extrabold tracking-wide">{label.toUpperCase()}</span>
                {on ? <Check size={16} /> : null}
              </button>
            );
          })}
        </div>
        {type === "delivery" && settings ? (
          <p className={`mt-2 font-sans text-[13px] ${belowMin || zoneMissing ? "text-hm-error" : "text-hm-muted"}`} data-testid="delivery-min-hint">
            {zoneMissing ? t("zoneNotServed") : `${t("minOrder")}${zone ? ` (${zone.city})` : ""}: ${chf(minimum)}${settings.delivery_fee > 0 ? ` · ${t("deliveryFee")}: ${chf(settings.delivery_fee)}` : ""}`}
          </p>
        ) : null}

        {/* Fidelity (loyalty) */}
        {user ? (
          <div className="mt-4 rounded-2xl border border-hm-brand/25 bg-hm-brand/5 p-4" data-testid="loyalty-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Gift size={18} className="text-hm-brand" />
                <span className="font-sans text-sm font-extrabold text-hm-ink">{t("loyaltyTitle")}</span>
              </div>
              {(loyaltyData?.rewards_available > 0 || quoteData?.rewards_applied > 0) ? (
                <span className="rounded-full bg-hm-brand px-3 py-1 font-sans text-xs font-bold text-white" data-testid="loyalty-reward-badge">{t("loyaltyRewardAvailable")}</span>
              ) : null}
            </div>
            {loyaltyData ? (
              <div className="mt-3 flex items-center gap-3">
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-hm-brand/15">
                  <div className="h-full rounded-full bg-hm-brand transition-all" style={{ width: `${Math.min(100, Math.round((loyaltyData.stamps / (loyaltyData.block || 10)) * 100))}%` }} />
                </div>
                <span className="font-sans text-sm font-bold text-hm-ink" data-testid="loyalty-progress">{loyaltyData.stamps}/{loyaltyData.block} {t("loyaltyStampsLabel")}</span>
              </div>
            ) : null}
            {quoteData?.stamps > 0 ? (
              <p className="mt-2 font-sans text-[13px] font-semibold text-hm-brand" data-testid="loyalty-earn">+{quoteData.stamps} {t("loyaltyStampsLabel")} {t("loyaltyEarn")}</p>
            ) : null}
            {quoteData?.rewards_applied > 0 ? (
              <p className="mt-2 font-sans text-[13px] font-bold text-emerald-600" data-testid="loyalty-reward-applied">{t("loyaltyRewardApplied")}</p>
            ) : quoteData?.rewards_kept > 0 ? (
              <p className="mt-2 font-sans text-[13px] font-bold text-hm-terracotta" data-testid="loyalty-reward-kept">{t("loyaltyRewardKept")}</p>
            ) : null}
            <p className="mt-2 font-sans text-xs text-hm-muted" data-testid="loyalty-rules">{t("loyaltyStampsRule")}</p>
          </div>
        ) : (
          <div className="mt-4 rounded-2xl border border-hm-line bg-hm-card p-4" data-testid="loyalty-guest-hint">
            <div className="flex items-center gap-2.5">
              <Gift size={18} className="flex-shrink-0 text-hm-brand" />
              <span className="font-sans text-[13px] text-hm-muted">{t("loyaltyGuestHint")}</span>
            </div>
            <p className="mt-2 font-sans text-xs text-hm-muted" data-testid="loyalty-rules-guest">{t("loyaltyStampsRule")}</p>
          </div>
        )}

        {/* Time */}
        <div ref={timeSectionRef} className="scroll-mt-24">
        <Section title={t("desiredTime")}>
          {closedToday ? (
            <div className="flex items-start gap-2.5 rounded-xl bg-hm-error p-3.5 text-white" data-testid="checkout-closed-today">
              <AlertTriangle size={18} className="mt-0.5 flex-shrink-0" />
              <span className="font-sans text-[13px] font-bold">{t("closedToday")}</span>
            </div>
          ) : (
          <>
          {closedNow ? (
            <div className="mb-3 flex items-center gap-2 rounded-xl bg-hm-error p-3 text-white" data-testid="checkout-closed">
              <Clock size={16} />
              <span className="font-sans text-[13px] font-bold">{t("restaurantClosed")}{ordering?.next_open ? ` · ${t("nextOpening")}: ${ordering.next_open}` : ""}</span>
            </div>
          ) : asapFrom ? (
            <p className="mb-3 font-sans text-[13px] text-hm-muted" data-testid="checkout-asap-from">{t("asapFromOpening")} {asapFrom}</p>
          ) : type === "delivery" && ordering && !ordering.delivery_open ? (
            <div className="mb-3 flex items-center gap-2 rounded-xl bg-hm-terracotta p-3 text-white" data-testid="checkout-delivery-unavailable">
              <Truck size={16} />
              <span className="font-sans text-[13px] font-bold">{t("deliveryUnavailable")}</span>
            </div>
          ) : type === "delivery" && ordering?.delivery_from ? (
            <p className="mb-3 font-sans text-[13px] text-hm-muted" data-testid="checkout-delivery-from">{t("earliestDelivery")}: {ordering.delivery_from}</p>
          ) : null}

          <div className="mb-3 flex gap-2 rounded-2xl bg-hm-tertiary p-1" data-testid="time-mode-segment">
            <button
              type="button"
              disabled={!asapAvailable}
              data-testid="time-mode-asap"
              onClick={() => setTimeMode("asap")}
              className={`flex h-11 flex-1 items-center justify-center gap-2 rounded-xl font-sans text-sm font-extrabold transition-colors ${timeMode === "asap" ? "bg-hm-inverse text-white" : "text-hm-ink"} ${!asapAvailable ? "opacity-40" : ""}`}
            >
              <Zap size={15} /> {t("asap")}{asapFrom ? ` · ${asapFrom}` : ""}
            </button>
            <button
              type="button"
              data-testid="time-mode-scheduled"
              onClick={() => setTimeMode("scheduled")}
              className={`flex h-11 flex-1 items-center justify-center gap-2 rounded-xl font-sans text-sm font-extrabold transition-colors ${timeMode === "scheduled" ? "bg-hm-inverse text-white" : "text-hm-ink"}`}
            >
              <Clock size={15} /> {t("chooseTime")}
            </button>
          </div>

          {timeMode === "scheduled" ? (
            <>
              {days.length > 1 ? (
                <div className="mb-3 flex flex-wrap gap-2" data-testid="day-picker">
                  {days.map((d, i) => (
                    <button key={d.date} type="button" data-testid={`day-${d.date}`} onClick={() => pickDay(i)} className={`h-10 rounded-full border px-4 font-sans text-sm font-semibold transition-colors ${dayIdx === i ? "border-hm-brand bg-hm-brand text-white" : "border-hm-line bg-hm-card text-hm-ink"}`}>
                      {dayLabel(d)}
                    </button>
                  ))}
                </div>
              ) : null}
              <div className="flex flex-wrap gap-2">
                {slots.length === 0 ? (
                  <p className="font-sans text-[13px] text-hm-muted" data-testid="checkout-no-slots">
                    {closedNow ? `${t("restaurantClosed")}${ordering?.next_open ? ` – ${ordering.next_open}` : ""}` : asapAvailable ? t("noExactSlotsAsap") : t("noExactSlots")}
                  </p>
                ) : null}
                {slots.map((s) => (
                  <button key={s} type="button" data-testid={`time-slot-${s.replace(":", "")}`} onClick={() => setTime(s)} className={`h-10 rounded-full border px-4 font-sans text-sm font-semibold transition-colors ${time === s ? "border-hm-brand bg-hm-brand text-white" : "border-hm-line bg-hm-card text-hm-ink"}`}>
                    {s}
                  </button>
                ))}
              </div>
              <p className="mt-3 font-sans text-[13px] text-hm-muted" data-testid="scheduled-hint">
                {isFutureDay && day ? `${t("scheduledFor")} ${dayLabel(day)} (${day.date})${time ? ` · ${time}` : ""}. ` : ""}{t("notGuaranteed")}
              </p>
            </>
          ) : null}
          </>
          )}
        </Section>
        </div>

        {/* Customer */}
        <Section title={t("yourDetails")}>
          <div className="grid grid-cols-2 gap-3">
            <Input label={`${t("firstName")} *`} value={f.first_name} onChange={set("first_name")} testId="checkout-first-name" />
            <Input label={t("lastName")} value={f.last_name} onChange={set("last_name")} testId="checkout-last-name" />
          </div>
          <Input label={`${t("phone")} *`} value={f.phone} onChange={set("phone")} placeholder="079 123 45 67" testId="checkout-phone" />
          <Input label={`${t("email")} *`} value={f.email} onChange={set("email")} placeholder="nom@exemple.ch" testId="checkout-email" />
          {f.email.trim() && !emailOk ? <p className="font-sans text-[13px] text-hm-error" data-testid="checkout-email-error">{t("invalidEmail")}</p> : null}
          <p className="font-sans text-[13px] text-hm-muted">{t("emailUsage")}</p>

          {type === "delivery" && user && user.addresses.length > 0 ? (
            <div className="mt-2">
              <p className="mb-2 font-sans text-[13px] text-hm-muted">{t("useSavedAddress")}</p>
              <div className="flex flex-wrap gap-2" data-testid="checkout-saved-addresses">
                {user.addresses.map((a) => (
                  <button key={a.id} type="button" data-testid={`checkout-saved-address-${a.id}`} onClick={() => pickSavedAddress(a.id)} className={`h-9 rounded-full border px-3.5 font-sans text-sm font-semibold ${savedAddrId === a.id ? "border-hm-brand bg-hm-brand text-white" : "border-hm-line bg-hm-card text-hm-ink"}`}>
                    {a.label || `${a.street} ${a.number}`.trim()}
                  </button>
                ))}
                <button type="button" data-testid="checkout-new-address" onClick={() => pickSavedAddress(null)} className={`h-9 rounded-full border px-3.5 font-sans text-sm font-semibold ${!savedAddrId ? "border-hm-brand bg-hm-brand text-white" : "border-hm-line bg-hm-card text-hm-ink"}`}>
                  {t("newAddress")}
                </button>
              </div>
            </div>
          ) : null}

          {type === "delivery" ? (
            <>
              <div className="grid grid-cols-[3fr_1fr] gap-3">
                <Input label={`${t("street")} *`} value={f.street} onChange={set("street")} testId="checkout-street" />
                <Input label={t("number")} value={f.number} onChange={set("number")} testId="checkout-number" />
              </div>
              <div className="grid grid-cols-[1fr_2fr] gap-3">
                <Input label={`${t("npa")} *`} value={f.npa} onChange={set("npa")} testId="checkout-npa" />
                <Input label={`${t("city")} *`} value={f.city} onChange={set("city")} testId="checkout-city" />
              </div>
              <Input label={t("deliveryInstructions")} value={f.instructions} onChange={set("instructions")} testId="checkout-instructions" />
              {user && !savedAddrId ? (
                <button type="button" data-testid="checkout-save-address" onClick={() => setSaveAddress((v) => !v)} className="flex items-center gap-3 text-left">
                  <span className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md border-2 ${saveAddress ? "border-hm-brand bg-hm-brand" : "border-hm-lineStrong"}`}>{saveAddress ? <Check size={14} className="text-white" /> : null}</span>
                  <span className="font-sans text-sm text-hm-ink">{t("saveThisAddress")}</span>
                </button>
              ) : null}
            </>
          ) : null}
        </Section>

        {cart.requiredAge ? (
          <button type="button" data-testid="age-confirm-checkbox" onClick={() => setAgeOk((v) => !v)} className="mt-5 flex w-full items-center gap-3 rounded-2xl bg-hm-terracotta/10 p-4 text-left">
            <span className={`flex h-6 w-6 items-center justify-center rounded-md border-2 ${ageOk ? "border-hm-brand bg-hm-brand" : "border-hm-terracotta"}`}>{ageOk ? <Check size={14} className="text-white" /> : null}</span>
            <span className="flex-1">
              <span className="block font-display text-lg text-hm-terracotta" data-testid="age-required-label">{cart.requiredAge}+</span>
              <span className="block font-sans text-sm font-semibold text-hm-ink">{t(cart.requiredAge === 18 ? "ageConfirm18" : "ageConfirm16")}</span>
            </span>
          </button>
        ) : null}

        {/* Payment */}
        <Section title={t("howToPay")}>
          <div className="grid grid-cols-2 gap-3">
            {[
              { m: "cash", Icon: DollarSign, label: t("payCash") },
              { m: "terminal", Icon: CreditCard, label: t("payCard") },
            ].map(({ m, Icon, label }) => {
              const on = payment === m;
              return (
                <button key={m} type="button" data-testid={`checkout-pay-${m}`} onClick={() => setPayment(m)} className={`flex h-14 items-center justify-center gap-2 rounded-2xl border-2 font-sans text-sm font-black tracking-wide transition-colors ${on ? "border-hm-brand bg-hm-brand text-white" : "border-hm-line bg-hm-card text-hm-ink"}`}>
                  <Icon size={18} className={on ? "text-white" : "text-hm-brand"} /> {label.toUpperCase()}
                </button>
              );
            })}
          </div>
          <div className="mt-3 flex items-center gap-3 rounded-2xl bg-hm-olive/10 p-4">
            <Info size={18} className="text-hm-olive" />
            <div className="flex-1">
              <p className="font-sans text-sm font-bold text-hm-olive" data-testid="payment-method-label">{type === "pickup" ? t("payAtPickup") : t("payAtDelivery")} · {payment === "cash" ? t("payCash") : t("payCard")}</p>
              <p className="font-sans text-xs text-hm-muted">{t("noOnlinePayment")}</p>
            </div>
          </div>
        </Section>

        {/* Summary */}
        <div className="mt-6 rounded-3xl bg-hm-inverse p-5 text-white">
          <Row label={t("subtotal")} value={chf(cart.subtotal)} />
          <Row label={t("extrasTotal")} value={chf(cart.extrasTotal)} />
          {loyaltyDiscount > 0 ? (
            <div className="flex justify-between" data-testid="loyalty-discount-row">
              <span className="font-sans text-sm font-bold text-emerald-300">{t("loyaltyTitle")} −{loyaltyPct}%</span>
              <span className="font-sans text-sm font-bold text-emerald-300" data-testid="loyalty-discount-value">−{chf(loyaltyDiscount)}</span>
            </div>
          ) : null}
          {type === "delivery" ? <Row label={t("deliveryFee")} value={fee === 0 ? t("free") : chf(fee)} /> : null}
          <div className="my-2 h-px bg-white/15" />
          <div className="flex justify-between">
            <span className="font-display text-2xl">{t("total")}</span>
            <span className="font-display text-2xl" data-testid="checkout-total">{chf(total)}</span>
          </div>
        </div>

        {/* Final order-type summary — makes LIVRAISON vs RETRAIT unmistakable right above the CTA */}
        <div className="mt-6 rounded-3xl border-2 border-hm-brand bg-hm-brand/5 p-5" data-testid="checkout-order-summary">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              {type === "delivery" ? <Truck size={26} className="flex-shrink-0 text-hm-brand" /> : <ShoppingBag size={26} className="flex-shrink-0 text-hm-brand" />}
              <div>
                <p className="font-display text-2xl leading-tight text-hm-ink" data-testid="checkout-summary-type">
                  {type === "delivery" ? t("delivery").toUpperCase() : t("pickupInStore").toUpperCase()}
                </p>
                <div className="mt-1 flex items-center gap-1.5">
                  <Clock size={15} className="text-hm-muted" />
                  <span className="font-sans text-sm font-semibold text-hm-ink" data-testid="checkout-summary-when">{whenText}</span>
                </div>
              </div>
            </div>
            <button
              type="button"
              data-testid="checkout-summary-edit"
              onClick={() => scrollToSection(type === "delivery" ? typeSectionRef : timeSectionRef)}
              className="flex-shrink-0 rounded-full border border-hm-lineStrong px-4 py-1.5 font-sans text-sm font-bold text-hm-ink transition-colors hover:bg-hm-tertiary"
            >
              {t("modify")}
            </button>
          </div>
          {type === "delivery" && (f.street || f.number || f.npa || f.city) ? (
            <p className="mt-3 font-sans text-[13px] text-hm-ink" data-testid="checkout-summary-address">
              {`${f.street} ${f.number}`.trim()}{(f.street || f.number) && (f.npa || f.city) ? ", " : ""}{`${f.npa} ${f.city}`.trim()}
            </p>
          ) : null}
        </div>
      </div>

      {/* Sticky CTA — real order submission (single POST, disabled while pending) */}
      <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-hm-line bg-hm-surface px-4 pt-3 pb-4">
        <div className="mx-auto w-full max-w-[760px]">
          <button
            type="button"
            data-testid="confirm-order-button"
            onClick={submit}
            disabled={place.isPending || !ORDERING_ENABLED || closedToday}
            aria-busy={place.isPending}
            className="flex h-14 w-full items-center justify-center gap-2 rounded-full bg-hm-brand font-sans text-base font-extrabold text-white transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-60"
          >
            {!ORDERING_ENABLED ? (
              <><AlertTriangle size={18} /> {t("orderingUnavailableTitle")}</>
            ) : closedToday ? (
              <><AlertTriangle size={18} /> {t("closedTodayShort")}</>
            ) : (
              <>
                {place.isPending ? (
                  <span className="h-5 w-5 animate-spin rounded-full border-[2.5px] border-white/40 border-t-white" data-testid="confirm-order-spinner" />
                ) : (
                  <Check size={18} />
                )}
                {t("confirmOrder")} · {chf(total)}
              </>
            )}
          </button>
          <p className="mt-2 text-center font-sans text-xs text-hm-muted">{type === "pickup" ? t("payAtPickup") : t("payAtDelivery")}</p>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-6 flex flex-col gap-3">
      <h2 className="font-display text-2xl text-hm-ink">{title}</h2>
      {children}
    </div>
  );
}

function Input({ label, value, onChange, placeholder, testId }) {
  return (
    <label className="block">
      <span className="mb-1.5 block font-sans text-[13px] font-semibold text-hm-ink">{label}</span>
      <input
        data-testid={testId}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="h-12 w-full rounded-xl border border-hm-line bg-hm-card px-3.5 font-sans text-base text-hm-ink outline-none focus:border-hm-brand"
      />
    </label>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between">
      <span className="font-sans text-sm text-white/80">{label}</span>
      <span className="font-sans text-sm font-semibold text-white">{value}</span>
    </div>
  );
}
